import { DataTable } from 'mantine-datatable';
import { UseFormReturnType } from '@mantine/form';
import { FieldWithValue, FormValuesType } from 'src/UI/dynamicUI/types';
import DynamicField from 'src/UI/dynamicUI/layout/DynamicField';
import React from 'react';
import { ListDeleteButton } from 'src/UI/dynamicUI/layout/DynamicEntitySection';
import { Tooltip } from '@mantine/core';

interface TableProps {
  form: UseFormReturnType<FormValuesType>;
  columns: {
    accessor: string;
    title?: string;
    onValueChange?: (value: unknown, fieldName: string, index: number) => void;
    disabled?: boolean;
    tooltip?: string;
    customRender?: (index: number) => React.ReactNode;
  }[];
  dataKey: string;
  showDeleteButton?: boolean;
}

export function FormTable({ columns, dataKey, form, showDeleteButton = true }: TableProps) {
  const formColumns = columns.map(c => ({
    accessor: c.accessor as string,
    title: c.title,
    render: (_: FieldWithValue, index: number) => {
      const { onValueChange, customRender } = c;

      if (customRender) {
        return customRender(index);
      }

      return (
        <Tooltip disabled={!c.tooltip} label={c.tooltip} position="top" withArrow>
          <span>
            <DynamicField
              field={{
                type: 'FIELD',
                span: 12,
                entityType: dataKey,
                entityField: c.accessor,
              }}
              disabled={c.disabled}
              index={index}
              hideLabel
              onValueChange={
                onValueChange
                  ? (value: unknown, fieldName: string) => onValueChange(value, fieldName, index)
                  : undefined
              }
            />
          </span>
        </Tooltip>
      );
    },
  }));

  if (showDeleteButton) {
    formColumns.push({
      accessor: 'actions',
      title: '',
      render: (_: FieldWithValue, index: number) => {
        return <ListDeleteButton index={index} entityType={dataKey} form={form} />;
      },
    });
  }

  const untypedItems = form.getValues()[dataKey];
  const items = Array.isArray(untypedItems) ? untypedItems : [];

  return (
    <DataTable
      withTableBorder={false}
      withColumnBorders={false}
      withRowBorders
      borderColor="gray.2"
      rowBorderColor="gray.2"
      striped
      highlightOnHover
      columns={formColumns}
      records={items}
      minHeight={200}
      variant={'form-table'}
      styles={{
        header: {
          backgroundColor: 'var(--mantine-color-gray-1)',
          fontSize: 'var(--mantine-font-size-xs)',
          fontWeight: 600,
          color: 'var(--mantine-color-gray-7)',
          marginBottom: 'var(--mantine-spacing-xs)',
        },
      }}
    />
  );
}
